import { useEffect, useState } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { clearEmployeeToken, getEmployeeData, employeeApi } from '../lib/employeeApi'
import { useFcm } from '../lib/useFcm'
import { Capacitor } from '@capacitor/core'
import { useEmployeeModules, ModuleKey } from '../lib/modules'
import { isKiosk } from '../lib/kiosk'

// module : entrée masquée si ce module est débranché ; ifOff : entrée affichée seulement si ce module est débranché
const BASE_NAV: { to: string; icon: string; label: string; badge: boolean; msgBadge?: boolean; module?: ModuleKey; ifOff?: ModuleKey }[] = [
  { to: '/app/home',          icon: '🏠', label: 'Accueil',  badge: false },
  { to: '/app/planning',      icon: '📅', label: 'Horaires', badge: false },
  { to: '/app/tasks',         icon: '✅', label: 'Tâches',   badge: false, ifOff: 'badgeuse' },
  { to: '/app/clock',         icon: '⏱',  label: 'Pointer',  badge: true,  module: 'badgeuse' },
  { to: '/app/absences',      icon: '🌴', label: 'Congés',   badge: false, module: 'conges' },
  { to: '/app/messages',      icon: '💬', label: 'Messages', badge: false, msgBadge: true },
]

export default function MobileLayout({ children }: { children: React.ReactNode }) {
  const nav = useNavigate()
  const location = useLocation()
  const emp = getEmployeeData()
  const isHome = location.pathname === '/app/home'
  const [badgeEnabled, setBadgeEnabled] = useState(true)
  const [unreadMessages, setUnreadMessages] = useState(0)
  const { modules } = useEmployeeModules()
  useFcm()

  useEffect(() => {
    employeeApi.get('/employee/me').then(r => {
      setBadgeEnabled(r.data?.establishment?.badgeEnabled !== false)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    function loadUnread() {
      employeeApi.get('/employee/me/messages-unread').then(r => setUnreadMessages(r.data?.count || 0)).catch(() => {})
    }
    loadUnread()
    const interval = setInterval(loadUnread, 20000)
    return () => clearInterval(interval)
  }, [])

  // Bouton retour Android — navigue en arrière dans l'app au lieu de quitter
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let removeListener: (() => void) | null = null
    import('@capacitor/app').then(({ App }) => {
      App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back()
        } else {
          App.exitApp()
        }
      }).then(handle => {
        removeListener = () => handle.remove()
      })
    })
    return () => { removeListener?.() }
  }, [])

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null
    if (link) link.href = '/manifest-employee.json'
    document.title = 'Mon Planning — Planner Utile'
    const meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null
    if (meta) meta.content = '#1e40af'
    return () => {
      if (link) link.href = '/manifest.json'
      document.title = 'Planner Utile'
      if (meta) meta.content = '#2563eb'
    }
  }, [])

  function logout() {
    clearEmployeeToken()
    nav('/app/login')
  }

  return (
    <div className="bg-gray-50 flex flex-col max-w-lg md:max-w-full mx-auto"
      style={{ minHeight: '100dvh' }}>
      {/* Header — respecte la barre de statut en haut */}
      <header className="bg-blue-600 text-white px-4 lg:px-8 flex items-center gap-3 sticky top-0 z-10 shadow-md"
        style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}>
        {!isHome && (
          <button onClick={() => nav('/app/home')} className="text-white text-xl leading-none pr-1">
            ←
          </button>
        )}
        <div className="flex-1">
          <div className="font-semibold">{emp?.firstName} {emp?.lastName !== '-' ? emp?.lastName : ''}</div>
          <div className="text-blue-200 text-xs">{emp?.establishment?.name}</div>
        </div>
        {isKiosk() ? (
          <button onClick={logout} className="bg-white text-blue-700 font-bold text-sm px-4 py-2 rounded-xl shadow">
            Terminer
          </button>
        ) : (
          <button onClick={logout} className="text-blue-200 text-sm hover:text-white">
            Déconnexion
          </button>
        )}
      </header>

      {/* Content — padding bas pour ne pas passer sous la nav */}
      <main className="flex-1 overflow-y-auto md:max-w-4xl md:mx-auto md:w-full"
        style={{ paddingBottom: 'calc(64px + env(safe-area-inset-bottom))' }}>
        {children}
      </main>

      {/* Bottom nav — respecte la barre de navigation Android */}
      <nav className="fixed bottom-0 left-0 right-0 max-w-lg md:max-w-full mx-auto bg-white border-t border-gray-200 flex z-10"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {BASE_NAV
          .filter(item => !item.badge || badgeEnabled)
          .filter(item => !item.module || modules[item.module])
          .filter(item => !item.ifOff || !modules[item.ifOff])
          .map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center py-2 text-xs transition-colors ${
                isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`
            }
          >
            <span className="relative inline-block text-xl">
              {item.icon}
              {(item as any).msgBadge && unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </span>
            <span className="mt-0.5">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
